import { createContainer } from "unstated-next";
import useRepository from "./useRepository";
import { useEffect } from "react";
import { useErrorHandler } from "./useErrorHandler";
import { UserUpdateLocationRequest } from "@travello/grpc-api/js/user_pb";
import { Subscription } from "rxjs";

import { useAuth } from "../auth/useAuth";
import { useGeolocation as _useGeolocation } from "react-use";
import { GeoLocationSensorState } from "react-use/lib/useGeolocation";
import { DeepNonNullable } from "utility-types";
import { isForeground, shouldHandleVisibilityChange } from "@/utils/utils";
import EmptyProvider from "@/utils/EmptyProvider";

/* eslint-disable react-hooks/exhaustive-deps */

const useGeoLocation_ = () => {
  const { userUpdateLocation$ } = useRepository();
  const { handleError } = useErrorHandler();
  const [{ user }] = useAuth();
  const { accuracy, longitude, latitude, loading } = _useGeolocation();

  const subscribeToGeolocation = ({
    latitude,
    longitude,
    accuracy
  }: DeepNonNullable<
    Pick<GeoLocationSensorState, "latitude" | "longitude" | "accuracy">
  >) => {
    const updateType = isForeground()
      ? UserUpdateLocationRequest.UpdateType.FOREGROUND
      : UserUpdateLocationRequest.UpdateType.BACKGROUND;

    return userUpdateLocation$({
      latLng: { latitude, longitude },
      accuracy: { distance: accuracy },
      updateType
    }).subscribe({
      error: handleError
    });
  };

  useEffect(() => {
    const Visibility = require("visibilityjs");

    let s: Subscription, listener: number;
    if (
      !user ||
      loading ||
      accuracy === null ||
      longitude === null ||
      latitude === null
    ) {
      return;
    }
    s = subscribeToGeolocation({ latitude, longitude, accuracy });
    const handleChange = () => {
      if (!shouldHandleVisibilityChange()) {
        return;
      }
      if (s) s.unsubscribe();
      s = subscribeToGeolocation({ latitude, longitude, accuracy });
    };

    listener = Visibility.change(handleChange) as number;

    return function cleanup() {
      if (s) {
        s.unsubscribe();
      }
      Visibility.unbind(listener);
    };
  }, [user, latitude, longitude]);

  const state = { loading, latitude, longitude };

  return [state] as [typeof state];
};

const GeoLocationState = createContainer(useGeoLocation_);
GeoLocationState.Provider.displayName = "GeoLocationState.Provider";

export const useGeolocation: typeof GeoLocationState["useContainer"] = navigator.geolocation
  ? () => GeoLocationState.useContainer()
  : () => [{ loading: false, latitude: null, longitude: null }];

const GeoLocationStateProvider: typeof GeoLocationState.Provider = navigator.geolocation
  ? GeoLocationState.Provider
  : EmptyProvider;

export default GeoLocationStateProvider;
